import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/base/SEO';

export default function AboutPage() {
  const { t } = useTranslation(['about', 'common']);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEO
        title={t('about:seo.title')}
        description={t('about:seo.description')}
        keywords={t('about:seo.keywords')}
        ogImage="https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/d1e4a11ecccff17e66a23f9227a9c530.jpeg"
      />
      <Navigation />

      <div className="pt-16 sm:pt-20">
        {/* Hero Section */}
        <section className="py-12 sm:py-20 px-5 sm:px-8 bg-white">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-2xl sm:text-4xl md:text-6xl font-light text-[#4A5D44] mb-5 sm:mb-8 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('about:hero.title')}
            </h1>
            <div className="w-16 sm:w-24 h-px bg-[#8B9D83] mx-auto mb-6 sm:mb-12"></div>
            <p className="text-sm sm:text-lg leading-relaxed text-gray-700 font-light max-w-3xl mx-auto">
              {t('about:hero.description')}
            </p>
          </div>
        </section>

        {/* Why Two Experts Section */}
        <section className="py-12 sm:py-24 px-5 sm:px-8 bg-[#F5F7F3]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-16">
              <h2 className="text-xl sm:text-3xl md:text-5xl font-light text-[#4A5D44] mb-4 sm:mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('about:whyTwo.title')}
              </h2>
              <div className="w-16 sm:w-24 h-px bg-[#8B9D83] mx-auto"></div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 sm:gap-12 mb-6 sm:mb-16">
              <div className="bg-white p-6 sm:p-10 rounded-sm shadow-sm">
                <div className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6">
                  <i className="ri-heart-pulse-line text-2xl sm:text-4xl text-[#8B9D83]" aria-hidden="true"></i>
                </div>
                <h3 className="text-lg sm:text-2xl font-light text-[#4A5D44] mb-3 sm:mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('about:whyTwo.doubleControl.title')}
                </h3>
                <p className="text-sm leading-relaxed text-gray-700 font-light">
                  {t('about:whyTwo.doubleControl.description')}
                </p>
              </div>

              <div className="bg-white p-6 sm:p-10 rounded-sm shadow-sm">
                <div className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6">
                  <i className="ri-microscope-line text-2xl sm:text-4xl text-[#8B9D83]" aria-hidden="true"></i>
                </div>
                <h3 className="text-lg sm:text-2xl font-light text-[#4A5D44] mb-3 sm:mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('about:whyTwo.deepExpertise.title')}
                </h3>
                <p className="text-sm leading-relaxed text-gray-700 font-light">
                  {t('about:whyTwo.deepExpertise.description')}
                </p>
              </div>
            </div>

            <div className="bg-white p-6 sm:p-12 rounded-sm shadow-sm text-center">
              <p className="text-base sm:text-xl leading-relaxed text-[#4A5D44] font-light italic max-w-4xl mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('about:whyTwo.quote')}
              </p>
            </div>
          </div>
        </section>

        {/* Expert Profiles */}
        <section className="py-12 sm:py-24 px-5 sm:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 sm:mb-20">
              <h2 className="text-xl sm:text-3xl md:text-5xl font-light text-[#4A5D44] mb-4 sm:mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('about:experts.title')}
              </h2>
              <div className="w-16 sm:w-24 h-px bg-[#8B9D83] mx-auto"></div>
            </div>

            {/* Sofia Profile */}
            <div className="grid md:grid-cols-2 gap-8 sm:gap-16 items-center mb-12 sm:mb-24">
              <div className="order-2 md:order-1">
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-xl sm:text-3xl font-light text-[#4A5D44] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {t('about:experts.sofia.name')}
                  </h3>
                  <p className="text-sm sm:text-lg text-[#8B9D83] font-light">
                    {t('about:experts.sofia.title')}
                  </p>
                </div>
                <div className="space-y-3 sm:space-y-6">
                  {[
                    t('about:experts.sofia.feature1'),
                    t('about:experts.sofia.feature2'),
                    t('about:experts.sofia.feature3'),
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3 sm:gap-4">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-line text-base sm:text-xl text-[#8B9D83]" aria-hidden="true"></i>
                      </div>
                      <p className="text-sm leading-relaxed text-gray-700 font-light">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="w-full h-64 sm:h-[500px] overflow-hidden rounded-sm shadow-lg">
                  <img
                    src="https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/d1e4a11ecccff17e66a23f9227a9c530.jpeg"
                    alt={t('about:experts.sofia.imageAlt')}
                    width="600"
                    height="800"
                    loading="lazy"
                    decoding="async"
                    className="w-full"
                    style={{
                      height: '220%',
                      objectFit: 'cover',
                      objectPosition: 'center 22%',
                      marginTop: '8%',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Elena Profile */}
            <div className="grid md:grid-cols-2 gap-8 sm:gap-16 items-center">
              <div className="order-1">
                <div className="w-full h-64 sm:h-[500px] overflow-hidden rounded-sm shadow-lg">
                  <img
                    src="https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/599164c3d435ec55cecdebc09fc7138a.png"
                    alt={t('about:experts.elena.imageAlt')}
                    width="600"
                    height="800"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full"
                    style={{ objectFit: 'cover', objectPosition: 'center top' }}
                  />
                </div>
              </div>
              <div className="order-2">
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-xl sm:text-3xl font-light text-[#4A5D44] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {t('about:experts.elena.name')}
                  </h3>
                  <p className="text-sm sm:text-lg text-[#8B9D83] font-light">
                    {t('about:experts.elena.title')}
                  </p>
                </div>
                <div className="space-y-3 sm:space-y-6">
                  {[
                    t('about:experts.elena.feature1'),
                    t('about:experts.elena.feature2'),
                    t('about:experts.elena.feature3'),
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3 sm:gap-4">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-line text-base sm:text-xl text-[#8B9D83]" aria-hidden="true"></i>
                      </div>
                      <p className="text-sm leading-relaxed text-gray-700 font-light">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-12 sm:py-24 px-5 sm:px-8 bg-[#F5F7F3]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 sm:mb-16">
              <h2 className="text-xl sm:text-3xl md:text-5xl font-light text-[#4A5D44] mb-5 sm:mb-8 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('about:philosophy.title')}
              </h2>
              <div className="w-16 sm:w-24 h-px bg-[#8B9D83] mx-auto mb-6 sm:mb-12"></div>
            </div>

            <div className="bg-white p-6 sm:p-12 rounded-sm shadow-sm mb-6 sm:mb-12">
              <p className="text-base sm:text-2xl leading-relaxed text-[#4A5D44] font-light text-center mb-5 sm:mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('about:philosophy.belief')}
              </p>
              <p className="text-sm leading-relaxed text-gray-700 font-light text-center max-w-3xl mx-auto">
                {t('about:philosophy.description')}
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 sm:gap-8">
              {[
                { icon: 'ri-book-open-line', title: t('about:philosophy.education.title'), text: t('about:philosophy.education.description') },
                { icon: 'ri-compass-3-line', title: t('about:philosophy.awareness.title'), text: t('about:philosophy.awareness.description') },
                { icon: 'ri-plant-line', title: t('about:philosophy.sustainability.title'), text: t('about:philosophy.sustainability.description') },
              ].map((item, i) => (
                <div key={i} className="bg-white p-5 sm:p-8 rounded-sm shadow-sm text-center">
                  <div className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <i className={`${item.icon} text-2xl sm:text-4xl text-[#8B9D83]`} aria-hidden="true"></i>
                  </div>
                  <h3 className="text-base sm:text-xl font-light text-[#4A5D44] mb-2 sm:mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm leading-relaxed text-gray-700 font-light">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-24 px-5 sm:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-xl sm:text-3xl md:text-5xl font-light text-[#4A5D44] mb-5 sm:mb-8 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('about:cta.title')}
            </h2>
            <p className="text-sm sm:text-lg leading-relaxed text-gray-700 font-light mb-7 sm:mb-12 max-w-2xl mx-auto">
              {t('about:cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
              <Link
                to="/payment"
                className="w-full sm:w-auto inline-block text-center px-7 sm:px-12 py-3 sm:py-4 bg-[#8B9D83] text-white text-xs sm:text-sm font-light tracking-widest uppercase rounded-sm hover:bg-[#7A8C72] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                aria-label={t('about:cta.primaryAria')}
              >
                {t('about:cta.primary')}
              </Link>
              <Link
                to="/services"
                className="w-full sm:w-auto inline-block text-center px-7 sm:px-12 py-3 sm:py-4 bg-transparent border-2 border-[#8B9D83] text-[#8B9D83] text-xs sm:text-sm font-light tracking-widest uppercase rounded-sm hover:bg-[#8B9D83] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                aria-label={t('about:cta.secondaryAria')}
              >
                {t('about:cta.secondary')}
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}